import { hubApi } from '@/lib/api';

import type {
  StandardLink,
  StandardsData,
  StandardsLookupValue,
} from './checkpoint.types';

export const fetchCheckpointStandards = async (): Promise<StandardsData> => {
  try {
    const versionedResponse = await hubApi.get<StandardsData>(
      '/governance/orr/checkpoints/underpinnings_versioned.json',
    );

    return versionedResponse.data;
  } catch {
    const legacyResponse = await hubApi.get<StandardsData>(
      '/governance/orr/checkpoints/underpinnings.json',
    );

    return legacyResponse.data;
  }
};

const isObjectRecord = (entry: unknown): entry is Record<string, unknown> =>
  Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry);

const sanitizeLinks = (entries: unknown[]): StandardLink[] => {
  const links: StandardLink[] = [];

  for (const entry of entries) {
    if (!isObjectRecord(entry) || typeof entry.url !== 'string') {
      continue;
    }

    const link: StandardLink = { url: entry.url };

    if (typeof entry.name === 'string') {
      link.name = entry.name;
    }

    if (typeof entry.description === 'string') {
      link.description = entry.description;
    }

    links.push(link);
  }

  return links;
};

export const normalizeStandards = (
  entry: StandardsLookupValue,
): StandardLink[] => {
  if (Array.isArray(entry)) {
    return sanitizeLinks(entry);
  }

  if (
    isObjectRecord(entry) &&
    'standards' in entry &&
    Array.isArray(entry.standards)
  ) {
    return sanitizeLinks(entry.standards);
  }

  if (
    isObjectRecord(entry) &&
    'underpinnings' in entry &&
    Array.isArray(entry.underpinnings)
  ) {
    return sanitizeLinks(entry.underpinnings);
  }

  return [];
};

const getVersionCandidates = (
  checkpointVersion: string | number | undefined,
): string[] => {
  if (checkpointVersion === undefined || checkpointVersion === null) {
    return [];
  }

  const rawVersion = String(checkpointVersion).trim();
  if (!rawVersion) {
    return [];
  }

  const normalizedVersion = rawVersion.toLowerCase();
  const withVPrefix = normalizedVersion.startsWith('v')
    ? normalizedVersion
    : `v${normalizedVersion}`;
  const withoutVPrefix = normalizedVersion.replace(/^v/, '');

  return Array.from(
    new Set([
      rawVersion,
      rawVersion.toLowerCase(),
      rawVersion.toUpperCase(),
      normalizedVersion,
      withVPrefix,
      withoutVPrefix,
    ]),
  );
};

const getStandardsFromVersionedEntry = (
  entry: StandardsLookupValue,
  checkpointVersion: string | number | undefined,
): StandardLink[] => {
  if (!isObjectRecord(entry)) {
    return [];
  }

  const versionCandidates = getVersionCandidates(checkpointVersion);

  for (const versionKey of versionCandidates) {
    const standards = normalizeStandards(
      entry[versionKey] as StandardsLookupValue,
    );
    if (standards.length > 0) {
      return standards;
    }
  }

  const fallbackVersionKeys = ['default', 'latest', 'current'];

  for (const fallbackVersionKey of fallbackVersionKeys) {
    const standards = normalizeStandards(
      entry[fallbackVersionKey] as StandardsLookupValue,
    );
    if (standards.length > 0) {
      return standards;
    }
  }

  for (const value of Object.values(entry)) {
    const standards = normalizeStandards(value as StandardsLookupValue);
    if (standards.length > 0) {
      return standards;
    }
  }

  return [];
};

export const getStandardsForCheckpoint = (
  checkpointId: string | undefined,
  standardsData: StandardsData,
  checkpointVersion?: string | number,
): Array<{ name: string; url: string }> => {
  if (!checkpointId) return [];

  const possibleCheckpointKeys = [
    checkpointId,
    checkpointId.toLowerCase(),
    checkpointId.toUpperCase(),
  ];

  const matchedStandards =
    possibleCheckpointKeys
      .map((checkpointKey) => {
        const checkpointEntry = standardsData[checkpointKey];

        const directStandards = normalizeStandards(checkpointEntry);
        if (directStandards.length > 0) {
          return directStandards;
        }

        return getStandardsFromVersionedEntry(
          checkpointEntry,
          checkpointVersion,
        );
      })
      .find((list) => list.length > 0) || [];

  return matchedStandards.map((item) => ({
    name: item.name || item.description || 'Standard/Policy',
    url: item.url,
  }));
};
